import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/stores/auth-store'
import { useDevicesStore } from '@/stores/devices-store'
import { AlertCircle, Check, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

interface ConfigField {
    key: string
    label: string
    type: 'text' | 'password' | 'url' | 'boolean'
    valueLabel?: string
    required?: boolean
}

const DEFAULT_CONFIG_FIELDS: ConfigField[] = [
    {
        key: 'apiUrl',
        label: 'API URL',
        type: 'url',
        required: true
    },
    {
        key: 'apiKey',
        label: 'API Key',
        type: 'password',
        required: true
    },
    {
        key: 'apiSecret',
        label: 'API Secret',
        type: 'password',
        required: false
    },
    {
        key: 'enabled',
        label: 'Integration aktiviert',
        type: 'boolean',
        valueLabel: 'Aktiviert'
    }
]

export function IntegrationConfig() {
    const { currentDevice, updateConfigData, getData } = useDevicesStore()
    const { canEdit } = useAuthStore()
    const { toast } = useToast()
    const [isLoading, setIsLoading] = useState(false)
    const [validationStatus, setValidationStatus] = useState<'success' | 'error' | null>(null)
    const [configFields] = useState<ConfigField[]>(DEFAULT_CONFIG_FIELDS)

    const {
        register,
        handleSubmit,
        formState: { errors, isDirty },
        reset,
        setValue,
        watch,
    } = useForm({
        defaultValues: {
            apiUrl: '',
            apiKey: '',
            apiSecret: '',
            enabled: false,
        },
    })

    const enabledValue = watch('enabled')

    useEffect(() => {
        if (currentDevice?.deviceId) {
            loadData()
        }
    }, [currentDevice?.deviceId])

    const loadData = () => {
        if (!currentDevice || !currentDevice.deviceConfiguration) {
            console.log('❌ IntegrationConfig: No device or config')
            return
        }

        reset({
            apiUrl: String(getData('INTEGRATION_API_URL') || ''),
            apiKey: String(getData('INTEGRATION_API_KEY') || ''),
            apiSecret: String(getData('INTEGRATION_API_SECRET') || ''),
            enabled: String(getData('INTEGRATION_ENABLED') || '') === 'true',
        })
    }

    const validateCredentials = async () => {
        // Simplified validation - in real app, this would call an API
        setValidationStatus('success')
        return true
    }

    const onSubmit = async (data: any) => {
        if (!isDirty) return

        setIsLoading(true)

        try {
            // Save all configuration fields
            await Promise.all([
                updateConfigData('INTEGRATION_API_URL', data.apiUrl),
                updateConfigData('INTEGRATION_API_KEY', data.apiKey),
                updateConfigData('INTEGRATION_API_SECRET', data.apiSecret || ''),
                updateConfigData('INTEGRATION_ENABLED', data.enabled ? 'true' : 'false'),
            ])

            // Validate if enabled
            if (data.enabled) {
                await validateCredentials()
            }

            toast({
                title: 'Erfolgreich gespeichert',
                description: 'Die Integrations-Konfiguration wurde aktualisiert',
            })

            reset(data)
        } catch (error) {
            console.error('Error saving integration config:', error)
            setValidationStatus('error')
            toast({
                variant: 'destructive',
                title: 'Fehler',
                description: 'Die Konfiguration konnte nicht gespeichert werden',
            })
        } finally {
            setIsLoading(false)
        }
    }

    const isDisabled = !canEdit() || isLoading

    return (
        <div className="space-y-6">
            {/* Validation Status */}
            {validationStatus && (
                <div
                    className={`rounded-lg p-4 ${validationStatus === 'success'
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-red-50 border border-red-200'
                        }`}
                >
                    <div className="flex items-center">
                        {validationStatus === 'success' ? (
                            <>
                                <Check className="w-5 h-5 text-green-500 mr-3" />
                                <p className="text-green-800 font-medium">
                                    API-Zugangsdaten erfolgreich validiert
                                </p>
                            </>
                        ) : (
                            <>
                                <X className="w-5 h-5 text-red-500 mr-3" />
                                <p className="text-red-800 font-medium">
                                    Fehler bei der Validierung der Zugangsdaten
                                </p>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Configuration Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {configFields.map((field) => (
                    <div key={field.key} className="space-y-2">
                        {field.type === 'boolean' ? (
                            <div className="flex items-center justify-between rounded-lg border p-4">
                                <div className="space-y-0.5">
                                    <Label htmlFor={field.key} className="text-sm font-medium">
                                        {field.label}
                                    </Label>
                                    <p className="text-sm text-gray-500">
                                        {field.valueLabel || 'Aktivieren/Deaktivieren'}
                                    </p>
                                </div>
                                <Switch
                                    id={field.key}
                                    checked={enabledValue}
                                    onCheckedChange={(checked) => setValue('enabled', checked, { shouldDirty: true })}
                                    disabled={isDisabled}
                                />
                            </div>
                        ) : (
                            <>
                                <Label htmlFor={field.key} className="text-sm font-semibold text-gray-700">
                                    {field.label}
                                    {field.required && <span className="text-red-500 ml-1">*</span>}
                                </Label>
                                <Input
                                    id={field.key}
                                    type={field.type === 'url' ? 'text' : field.type}
                                    {...register(field.key as any, {
                                        required: field.required ? 'Dieses Feld ist erforderlich' : false,
                                        pattern: field.type === 'url' ? {
                                            value: /^https?:\/\/.+/,
                                            message: 'Bitte geben Sie eine gültige URL ein'
                                        } : undefined
                                    })}
                                    placeholder={
                                        field.type === 'url'
                                            ? 'https://api.example.com'
                                            : field.type === 'password'
                                                ? '••••••••'
                                                : `${field.label} eingeben...`
                                    }
                                    disabled={isDisabled}
                                    className={errors[field.key as keyof typeof errors] ? 'border-red-300' : ''}
                                />
                                {errors[field.key as keyof typeof errors] && (
                                    <p className="text-sm text-red-600 font-medium flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" />
                                        {errors[field.key as keyof typeof errors]?.message}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                ))}

                {/* Info Note */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                        <strong>Hinweis:</strong> Diese Einstellungen konfigurieren die Verbindung zu externen
                        Reservierungssystemen oder APIs. Stellen Sie sicher, dass die Zugangsdaten korrekt sind.
                    </p>
                </div>

                {/* Submit Button */}
                <div className="pt-6 border-t border-gray-200">
                    <Button
                        type="submit"
                        disabled={!isDirty || isDisabled}
                        className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primary/90"
                    >
                        <Check className="w-4 h-4" />
                        {isLoading ? 'Speichert...' : 'Änderungen speichern'}
                    </Button>
                </div>
            </form>
        </div>
    )
}
